import { useState } from 'react';
import { BadgeDollarSign, Camera, ShieldCheck, Smartphone, X } from 'lucide-react';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { Badge, Button, Callout, Field, Input, PhoneInput, Select, Textarea, cx, useToast } from './ui.jsx';

const EMPTY = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  city: '',
  state: 'FL',
  zip: '',
  experienceYears: 2,
  radiusMiles: 25,
  kind: 'individual',
  crewName: '',
  hasVehicle: true,
  hasPpe: true,
  weekendReady: true,
  nightsReady: false,
  skills: [],
  equipment: [],
  referredBy: '',
  photoDataUrl: null,
  bio: '',
};

/**
 * The lumper application. Used as a full page during signup and inside a modal
 * from the marketing site, so the wording only has to be right in one place.
 */
export default function LumperApplyForm({ onDone, compact = false }) {
  const toast = useToast();
  const { data: config } = useApi('/public/config');
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const toggle = (key, value) =>
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter((v) => v !== value) : [...prev[key], value],
    }));

  const pickPhoto = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.err('That photo is too big', 'Please use one under 6MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set('photoDataUrl', reader.result);
    reader.readAsDataURL(file);
  };

  const next = () => {
    if (step === 0) {
      if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
        setError('We need your name and email to get started.');
        return;
      }
      if (form.password.length < 6) {
        setError('Pick a password with at least 6 characters.');
        return;
      }
    }
    if (step === 1) {
      // Without a ZIP we cannot work out which jobs are near them.
      if (!form.zip.trim()) {
        setError('We need your ZIP code — it is how we know which jobs are near you.');
        return;
      }
    }
    setError(null);
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/public/lumper-apply', {
        ...form,
        experienceYears: Number(form.experienceYears),
        radiusMiles: Number(form.radiusMiles),
      });
      toast.ok('You are signed up', 'We will check your details and get you working.');
      onDone?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const skills = config?.skills || [];
  const equipment = config?.equipment || [];

  return (
    <div className={cx('apply-flow', compact && 'compact')}>
      <div className="apply-steps">
        {['You', 'Where you work', 'What you can do'].map((label, index) => (
          <div key={label} className={cx('apply-step', index <= step && 'on')}>
            <span className="apply-step-num">{index + 1}</span>
            <span className="apply-step-label">{label}</span>
          </div>
        ))}
      </div>

      {error ? <Callout tone="red" className="mb-16">{error}</Callout> : null}

      {step === 0 ? (
        <div className="apply-form">
          <Field label="First name" required>
            <Input value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
          </Field>
          <Field label="Last name" required>
            <Input value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
          </Field>
          <Field label="Mobile number" required hint="This is how we send you work">
            <PhoneInput value={form.phone} onChange={(value) => set('phone', value)} placeholder="(305) 123-4567" />
          </Field>
          <Field label="Email" required>
            <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
          </Field>
          <Field label="Make a password" required hint="You will use this in the app">
            <Input type="password" value={form.password} onChange={(e) => set('password', e.target.value)} />
          </Field>
          <Field label="Who sent you here? (optional)">
            <Input value={form.referredBy} onChange={(e) => set('referredBy', e.target.value)} placeholder="Another lumper, a driver, Indeed…" />
          </Field>

          <div className="span-2">
            <span className="label">Your photo (optional)</span>
            <div className="row gap-12 mt-8">
              {form.photoDataUrl ? (
                <div className="photo-pick has-photo">
                  <img src={form.photoDataUrl} alt="Your photo" />
                  <button type="button" onClick={() => set('photoDataUrl', null)} aria-label="Remove photo">
                    <X size={13} strokeWidth={2.6} />
                  </button>
                </div>
              ) : (
                <label className="photo-pick">
                  <Camera size={20} />
                  <span>Add a photo</span>
                  <input type="file" accept="image/*" onChange={pickPhoto} hidden />
                </label>
              )}
              <span className="dim" style={{ fontSize: 'var(--fs-sm)', maxWidth: 320 }}>
                A clear photo of your face helps the office and the warehouses know who is coming.
              </span>
            </div>
          </div>
        </div>
      ) : null}

      {step === 1 ? (
        <div className="apply-form">
          {/* ZIP first and full width — it is the field that decides which jobs
              a lumper sees, so it must not be missable on a phone. */}
          <Field
            label="Your ZIP code"
            required
            hint="This is how we know which jobs are near you"
            className="span-2"
          >
            <Input
              value={form.zip}
              onChange={(e) => set('zip', e.target.value)}
              inputMode="numeric"
              placeholder="33166"
            />
          </Field>
          <Field label="City (optional)">
            <Input value={form.city} onChange={(e) => set('city', e.target.value)} />
          </Field>
          <Field label="State">
            <Input value={form.state} onChange={(e) => set('state', e.target.value)} maxLength={2} />
          </Field>
          <Field label="How many years have you done this?">
            <Input className="mono" type="number" value={form.experienceYears} onChange={(e) => set('experienceYears', e.target.value)} />
          </Field>
          <Field label="How far will you travel?">
            <Select
              value={String(form.radiusMiles)}
              onChange={(e) => set('radiusMiles', e.target.value)}
              options={[15, 20, 25, 30, 40, 50, 75].map((m) => ({ value: String(m), label: `${m} miles` }))}
            />
          </Field>
          <Field label="Are you signing up as">
            <Select
              value={form.kind}
              onChange={(e) => set('kind', e.target.value)}
              options={[
                { value: 'individual', label: 'Just me' },
                { value: 'crew', label: 'Me and my crew' },
                { value: 'vendor', label: 'A labor company' },
              ]}
            />
          </Field>
          {form.kind !== 'individual' ? (
            <Field label="Crew or company name" className="span-2">
              <Input value={form.crewName} onChange={(e) => set('crewName', e.target.value)} />
            </Field>
          ) : null}
          <div className="span-2 row gap-20 wrap">
            <label className="check">
              <input type="checkbox" checked={form.hasVehicle} onChange={(e) => set('hasVehicle', e.target.checked)} />
              <span>I have my own transport</span>
            </label>
            <label className="check">
              <input type="checkbox" checked={form.hasPpe} onChange={(e) => set('hasPpe', e.target.checked)} />
              <span>I have boots and a hi-vis vest</span>
            </label>
            <label className="check">
              <input type="checkbox" checked={form.weekendReady} onChange={(e) => set('weekendReady', e.target.checked)} />
              <span>I can work weekends</span>
            </label>
            <label className="check">
              <input type="checkbox" checked={form.nightsReady} onChange={(e) => set('nightsReady', e.target.checked)} />
              <span>I can work nights</span>
            </label>
          </div>
        </div>
      ) : null}

      {step === 2 ? (
        <div className="col gap-20">
          <div>
            <span className="label">What work can you do?</span>
            <div className="wrap-gap mt-8">
              {skills.map((skill) => (
                <button
                  key={skill}
                  type="button"
                  className={cx('skill-pill', form.skills.includes(skill) && 'on')}
                  onClick={() => toggle('skills', skill)}
                >
                  {skill}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="label">What do you bring with you?</span>
            <div className="wrap-gap mt-8">
              {equipment.map((item) => (
                <button
                  key={item}
                  type="button"
                  className={cx('skill-pill', form.equipment.includes(item) && 'on')}
                  onClick={() => toggle('equipment', item)}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
          <Field label="Anything else we should know? (optional)">
            <Textarea
              rows={2}
              value={form.bio}
              onChange={(e) => set('bio', e.target.value)}
              placeholder="I have my own electric pallet jack and can bring 3 more people."
            />
          </Field>
          <Callout tone="blue" icon={ShieldCheck} title="What happens next">
            We run a quick background check and ask for your tax form and ID. Most people are working within a day.
          </Callout>
          <div className="row gap-12 wrap">
            <Badge tone="amber"><Smartphone size={12} /> Get work on your phone</Badge>
            <Badge tone="green"><BadgeDollarSign size={12} /> Paid every Friday</Badge>
          </div>
        </div>
      ) : null}

      <div className="apply-actions">
        {step > 0 ? (
          <Button variant="ghost" onClick={() => setStep((s) => s - 1)}>Back</Button>
        ) : null}
        <div className="grow" />
        {step < 2 ? (
          <Button variant="primary" size="lg" onClick={next}>Continue</Button>
        ) : (
          <Button variant="primary" size="lg" loading={busy} onClick={submit}>Sign me up</Button>
        )}
      </div>
    </div>
  );
}
